<script lang="ts">
	import type { Snippet } from 'svelte';

	export type ModernBadgeVariant =
		| 'warning'
		| 'success'
		| 'error'
		| 'rose'
		| 'info'
		| 'navy'
		| 'indigo'
		| 'teal'
		| 'yellow'
		| 'primary'
		| 'purple'
		| 'secondary'
		| 'neutral'
		| 'pack'
		| 'giftcard'
		| 'admin';

	interface Props {
		variant?: ModernBadgeVariant;
		glassDesign?: boolean;
		icon?: Snippet;
		className?: string;
		children?: Snippet;
	}

	let {
		variant = 'neutral',
		glassDesign = false,
		icon = undefined,
		className = '',
		children
	}: Props = $props();

	const variantClasses: Record<ModernBadgeVariant, string> = {
		warning:
			'bg-amber-500/10 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/25 dark:border-amber-400/30',
		yellow: 'bg-yellow-500/15 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-200 border-yellow-500/30 dark:border-yellow-400/35',
		success:
			'bg-emerald-500/10 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/25 dark:border-emerald-400/30',
		error: 'bg-rose-500/10 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/25 dark:border-rose-400/30',
		rose: 'bg-rose-500/10 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/25 dark:border-rose-400/30',
		info: 'bg-sky-500/10 dark:bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/25 dark:border-sky-400/30',
		navy: 'bg-blue-600/10 dark:bg-blue-400/15 text-blue-800 dark:text-blue-200 border-blue-600/25 dark:border-blue-400/35',
		indigo: 'bg-indigo-500/10 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/25 dark:border-indigo-400/30',
		teal: 'bg-teal-500/10 dark:bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/25 dark:border-teal-400/30',
		primary:
			'bg-primary/10 dark:bg-primary/15 text-primary dark:text-primary-light border-primary/25 dark:border-primary-light/30',
		purple: 'bg-purple-500/10 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/25 dark:border-purple-400/30',
		secondary:
			'bg-slate-500/10 dark:bg-slate-400/15 text-slate-700 dark:text-slate-200 border-slate-500/25 dark:border-slate-400/35',
		neutral:
			'bg-slate-500/10 dark:bg-slate-400/15 text-slate-700 dark:text-slate-200 border-slate-500/25 dark:border-slate-400/35',
		pack: 'bg-purple-500/10 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/25 dark:border-purple-400/30',
		giftcard:
			'bg-amber-500/10 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/25 dark:border-amber-400/30',
		admin: 'bg-sky-600/10 dark:bg-sky-400/15 text-sky-800 dark:text-sky-200 border-sky-600/25 dark:border-sky-400/35'
	};

	const glassVariantClasses: Record<ModernBadgeVariant, string> = {
		warning:
			'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs',
		yellow: 'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs',
		success:
			'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs',
		error: 'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs',
		rose: 'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs',
		info: 'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs',
		navy: 'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs',
		indigo: 'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs',
		teal: 'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs',
		primary:
			'bg-white/25 text-current border-white/40 dark:bg-white/15 dark:border-white/25 shadow-2xs backdrop-blur-xs',
		purple: 'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs',
		secondary:
			'bg-white/15 text-current border-white/25 dark:bg-white/10 dark:border-white/15 shadow-2xs backdrop-blur-xs',
		neutral:
			'bg-white/15 text-current border-white/25 dark:bg-white/10 dark:border-white/15 shadow-2xs backdrop-blur-xs',
		pack: 'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs',
		giftcard:
			'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs',
		admin: 'bg-white/20 text-current border-white/35 dark:bg-white/10 dark:border-white/20 shadow-2xs backdrop-blur-xs'
	};
</script>

<span
	class="inline-flex items-center justify-center self-center rounded-full border text-xs py-0.5 px-2 gap-1.5 font-medium flex-none leading-none select-none transition-colors {glassDesign
		? glassVariantClasses[variant]
		: variantClasses[variant]} {className}"
>
	{#if icon}
		{@render icon()}
	{/if}
	{#if children}
		{@render children()}
	{/if}
</span>
